import React from 'react';
import './LoginForm.scss';
import Input from 'components/ui/Input';
import Checkbox from 'components/ui/Checkbox';
import Button from 'components/ui/Button';
import Switch from 'components/ui/Switch';
import { showToast } from 'components/redesign/Toast';
import { useAppDispatch } from 'store/store';
import {
    asyncSendStudentCode,
    asyncSendEmployeeCode,
    asyncSendEmail,
} from 'store/reducers/user/thunks';
import { SwitchValueProps } from 'components/ui/Switch/Switch';

const RESEND_COOLDOWN_S = 30;

interface StudentLoginFormProps {
    email: string;
    setEmail: React.Dispatch<React.SetStateAction<string>>;
    code: string;
    setCode: React.Dispatch<React.SetStateAction<string>>;
    dispatch: ReturnType<typeof useAppDispatch>;
    codeError: boolean;
    setCodeError: React.Dispatch<React.SetStateAction<boolean>>;
    pending: boolean;
}

function CodeField({
    code,
    setCode,
    codeError,
    setCodeError,
    label,
}: {
    code: string;
    setCode: React.Dispatch<React.SetStateAction<string>>;
    codeError: boolean;
    setCodeError: React.Dispatch<React.SetStateAction<boolean>>;
    label: string;
}): React.ReactElement {
    return (
        <div className="rd-login-form__code">
            <label
                className="rd-login-form__code-label"
                htmlFor="rd-login-code"
            >
                {label}
            </label>
            <input
                id="rd-login-code"
                className={`rd-login-form__code-input${
                    codeError ? ' rd-login-form__code-input--error' : ''
                }`}
                value={code}
                autoComplete="one-time-code"
                inputMode="text"
                placeholder="ABC123"
                onChange={e => {
                    setCode(e.target.value);
                    setCodeError(false);
                }}
            />
        </div>
    );
}

function StudentLoginForm({
    email,
    setEmail,
    code,
    setCode,
    dispatch,
    codeError,
    setCodeError,
    pending,
}: StudentLoginFormProps): React.ReactElement {
    const [emailError, setEmailError] = React.useState(false);
    const [cooldown, setCooldown] = React.useState(0);
    const [sending, setSending] = React.useState(false);
    const sendingRef = React.useRef(false);

    React.useEffect(() => {
        if (cooldown <= 0) return undefined;
        const id = window.setTimeout(() => setCooldown(c => c - 1), 1000);
        return () => window.clearTimeout(id);
    }, [cooldown]);

    const onEmailSentSuccess = React.useCallback(() => {
        setCooldown(RESEND_COOLDOWN_S);
        if (email.includes('@gmail.com')) {
            showToast(
                'Письмо отправлено. На почтах @gmail.com письма иногда ' +
                    'приходят с задержкой',
                'info'
            );
        } else {
            showToast('Письмо с кодом отправлено', 'info');
        }
    }, [email]);

    const onEmailSentError = React.useCallback(() => {
        showToast(
            'Не удалось отправить код. Проверь почту и соединение, ' +
                'затем повтори попытку',
            'warning'
        );
        setEmailError(true);
    }, []);

    const onSubmit = React.useCallback(() => {
        if (sendingRef.current) return;
        sendingRef.current = true;
        setSending(true);
        dispatch(asyncSendEmail(email))
            .unwrap()
            .then(onEmailSentSuccess)
            .catch(onEmailSentError)
            .finally(() => {
                sendingRef.current = false;
                setSending(false);
            });
    }, [dispatch, email, onEmailSentError, onEmailSentSuccess]);

    return (
        <>
            <Input
                value={email}
                onChange={e => setEmail(e.target.value)}
                submitLabel={
                    cooldown > 0
                        ? `Ещё раз через ${cooldown}с`
                        : 'Отправить код'
                }
                disabled={cooldown > 0 || sending || pending}
                error={emailError}
                setError={setEmailError}
                onSubmit={onSubmit}
                placeholder="student@bonch.ru"
                label="Email из личного кабинета СПбГУТ"
            />
            <CodeField
                code={code}
                setCode={setCode}
                codeError={codeError}
                setCodeError={setCodeError}
                label="Код подтверждения"
            />
        </>
    );
}

interface EmployeeLoginFormProps {
    code: string;
    setCode: React.Dispatch<React.SetStateAction<string>>;
    codeError: boolean;
    setCodeError: React.Dispatch<React.SetStateAction<boolean>>;
}

function EmployeeLoginForm({
    code,
    setCode,
    codeError,
    setCodeError,
}: EmployeeLoginFormProps): React.ReactElement {
    return (
        <CodeField
            code={code}
            setCode={setCode}
            codeError={codeError}
            setCodeError={setCodeError}
            label="Персональный код"
        />
    );
}

interface LoginFormProps {
    onSuccess: () => Promise<void>;
}

const switchValues: SwitchValueProps[] = [
    {
        key: 'student',
        name: 'Я студент',
    },
    {
        key: 'employee',
        name: 'Я сотрудник',
    },
];

function LoginForm({ onSuccess }: LoginFormProps): React.ReactElement {
    const dispatch = useAppDispatch();

    const [agreement, setAgreement] = React.useState(false);
    const [email, setEmail] = React.useState('');
    const [code, setCode] = React.useState('');
    const [codeError, setCodeError] = React.useState(false);
    const [pending, setPending] = React.useState(false);
    const pendingRef = React.useRef(false);
    const [switchValue, setSwitchValue] = React.useState(switchValues[0].key);
    const onSwitch = React.useCallback((next: string) => {
        setSwitchValue(next);
        setCode('');
        setCodeError(false);
    }, []);

    const sendCode = React.useCallback(() => {
        if (switchValue === 'employee')
            return asyncSendEmployeeCode(code.trim());
        return asyncSendStudentCode(code.trim());
    }, [code, switchValue]);

    const onLogin = React.useCallback(async () => {
        if (pendingRef.current) return;
        pendingRef.current = true;
        setPending(true);
        try {
            try {
                await dispatch(sendCode()).unwrap();
            } catch (error) {
                if (String(error).includes('Network Error')) {
                    showToast(
                        'Нет связи с сервером. Повтори попытку позже',
                        'warning'
                    );
                } else {
                    setCodeError(true);
                    showToast('Неправильно введён код', 'warning');
                }
                return;
            }
            try {
                await onSuccess();
            } catch (error) {
                showToast(
                    'Вход выполнен, но данные не загрузились. Обнови страницу',
                    'warning'
                );
            }
        } finally {
            pendingRef.current = false;
            setPending(false);
        }
    }, [dispatch, onSuccess, sendCode]);

    return (
        <div className="rd-login-form">
            <Switch
                values={switchValues}
                value={switchValue}
                onChange={onSwitch}
                maxWidth="100%"
            />

            {switchValue === 'student' && (
                <StudentLoginForm
                    email={email}
                    setEmail={setEmail}
                    code={code}
                    setCode={setCode}
                    dispatch={dispatch}
                    codeError={codeError}
                    setCodeError={setCodeError}
                    pending={pending}
                />
            )}

            {switchValue === 'employee' && (
                <EmployeeLoginForm
                    code={code}
                    setCode={setCode}
                    codeError={codeError}
                    setCodeError={setCodeError}
                />
            )}

            <Checkbox value={agreement} onChange={value => setAgreement(value)}>
                Я согласен(на) на{' '}
                <a
                    href="https://www.sut.ru"
                    target="_blank"
                    rel="noreferrer"
                    onClick={e => e.stopPropagation()}
                >
                    обработку персональных данных
                </a>
            </Checkbox>
            <Button
                disabled={!agreement || !code.trim() || pending}
                onClick={onLogin}
                color="green"
                size="m"
                block
            >
                Войти
            </Button>
        </div>
    );
}

export default React.memo(LoginForm);
